import { Button, Card, Col, DatePicker, Input, Modal, Space, Table, theme } from 'antd'
import React from 'react'
import CustomRow from '../../reusable-components/CustomRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import { hexToRGBA } from '../../lib/utils'
import CustomLoadingIcon from '../../reusable-components/CustomLoadingIcon'
import SystemUsersTemplates from '../../Admin/system-users/utils/templates'
import usePatientEnrollment from './hooks/usePatientEnrollment'
import PatientPlanOverview from './PatientPlanOverview'

const PatientEnrollment = ({ template, setCurrentTemplate, planType, planLabel, isMonitoring }) => {
  const { token } = theme.useToken()

  const {
    enrolledUsers,
    handleFetchEnrolledUsers,
    handleNewEnrollment,
    handleCancelEnrollment,
    loadingPatients,
    currentPatient,
    setCurrentPatient,
    newEnrollment,
    setNewEnrollment,
    // Enrollment search related state
    setSearchQuery,
    patient,
    setPatient,
    searchResults,
    handleEnrollPatient,
    searching,
    enrolling,
    enrollmentOverride,
    setEnrollmentOverride,
    handleTeamMemberSelection,
    teamMembers,
    handlePatientQuery,
    handleReset
  } = usePatientEnrollment({ template, planType })

  // EnrolledPatients component logic
  const renderEnrolledPatients = () => (
    <Card
      style={{ marginTop: 12 }}
      title={
        template?.name +
          (currentPatient
            ? `: ${currentPatient.patientProfile.firstName} ${currentPatient.patientProfile.lastName}`
            : '') || 'NO NAME FOUND'
      }
      size='small'
      headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
    >
      <CustomRow>
        {currentPatient ? (
          <Col span={24}>
            <PatientPlanOverview
              setCurrentPatient={setCurrentPatient}
              currentPatient={currentPatient}
              template={template}
              handleRefresh={handleFetchEnrolledUsers}
              planType={planType}
              planLabel={planLabel}
            />
          </Col>
        ) : (
          <>
            <Col span={8}>
              <Card
                headStyle={{ background: hexToRGBA(token.colorSecondary, 0.2) }}
                style={{ marginBottom: 8 }}
                title='Visits'
                size='small'
              >
                <Space direction='vertical'>
                  {template?.tracking
                    ?.filter((i) => i.trackerType === 'visits')
                    ?.map((tracker, idx) => (
                      <p key={idx}>{tracker.dataLabel}</p>
                    ))}
                </Space>
              </Card>
              <Card
                style={{ marginBottom: 8 }}
                headStyle={{ background: hexToRGBA(token.colorSecondary, 0.2) }}
                title='Trackers'
                size='small'
              >
                <Space direction='vertical'>
                  {template?.tracking
                    ?.filter((i) => i.trackerType === 'data')
                    ?.map((tracker, idx) => (
                      <p key={idx}>{tracker.dataLabel}</p>
                    ))}
                </Space>
              </Card>

              {planType === 'treatment' && (
                <>
                  <Card
                    style={{ marginBottom: 8 }}
                    headStyle={{ background: hexToRGBA(token.colorSecondary, 0.2) }}
                    title='Medications'
                    size='small'
                  >
                    <Space direction='vertical'>
                      {template?.medications?.length > 0 ? (
                        template.medications.map((medication, idx) => (
                          <p key={idx}>
                            {medication.name} - {medication.dosage}
                          </p>
                        ))
                      ) : (
                        <p>No medications specified</p>
                      )}
                    </Space>
                  </Card>

                  <Card
                    style={{ marginBottom: 8 }}
                    headStyle={{ background: hexToRGBA(token.colorSecondary, 0.2) }}
                    title='Diagnostic Tests'
                    size='small'
                  >
                    <Space direction='vertical'>
                      {template?.diagnosticTests?.length > 0 ? (
                        template.diagnosticTests.map((test, idx) => (
                          <p key={idx}>
                            {test.testName} - {test.testType}
                          </p>
                        ))
                      ) : (
                        <p>No diagnostic tests specified</p>
                      )}
                    </Space>
                  </Card>
                </>
              )}
            </Col>
            <Col span={16}>
              <Card
                extra={
                  <Space>
                    <Button
                      type='primary'
                      style={{ background: token.colorSuccess }}
                      onClick={() => {
                        setNewEnrollment(true)
                      }}
                    >
                      Enroll Patient
                    </Button>
                    <Button
                      type='primary'
                      onClick={() => {
                        handleFetchEnrolledUsers()
                      }}
                    >
                      <FontAwesomeIcon icon={faRefresh} />
                    </Button>
                  </Space>
                }
                size='small'
                title='Enrolled Patients'
                headStyle={{ background: hexToRGBA(token.colorPrimary, 0.1) }}
              >
                <Table
                  size='small'
                  bordered
                  onRow={(record) => {
                    return {
                      onClick: () => {
                        setCurrentPatient(record)
                      }
                    }
                  }}
                  loading={{
                    indicator: <CustomLoadingIcon />,
                    spinning: loadingPatients
                  }}
                  dataSource={enrolledUsers}
                  columns={[
                    {
                      title: 'Name',
                      key: 'patientProfile',
                      render: (text, record) => {
                        return (
                          <>
                            {record.patientProfile?.firstName} {record.patientProfile?.lastName}
                          </>
                        )
                      }
                    },
                    {
                      title: 'Enrolled',
                      key: 'audit',
                      render: (text, record) => {
                        return <>{new Date(record.audit?.createdAt).toLocaleDateString()}</>
                      }
                    },
                    {
                      title: 'Actions',
                      render: (text, record) => {
                        return (
                          <Space>
                            <Button
                              size='small'
                              danger
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCancelEnrollment(record)
                              }}
                            >
                              Cancel
                            </Button>
                          </Space>
                        )
                      }
                    }
                  ]}
                />
              </Card>
            </Col>
          </>
        )}
      </CustomRow>
    </Card>
  )

  // EnrollmentSearch component logic
  const renderEnrollmentSearch = () => (
    <>
      <div
        style={{
          width: '100%',
          padding: 8,
          background: hexToRGBA(token.colorPrimary, 0.2),
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          top: 0,
          left: 0
        }}
      >
        <h2>NEW {planLabel || 'PLAN'} ENROLLMENT</h2>
        <Button
          style={{ fontSize: 24, background: 'transparent', border: 'none' }}
          onClick={() => setNewEnrollment(false)}
        >
          <FontAwesomeIcon icon={faXmarkCircle} color={token.colorError} />
        </Button>
      </div>
      <CustomRow style={{ marginTop: 48 }}>
        {!patient ? (
          <>
            <Col span={24}>
              Search:{' '}
              <Input
                placeholder='Patient Name or ID...'
                onChange={(e) => handlePatientQuery(e.target.value)}
                style={{ width: 350, maxWidth: '100%' }}
              />
            </Col>
            <Col span={24}>
              <Table
                size='small'
                bordered
                onRow={(record) => {
                  return {
                    onClick: () => {
                      setSearchQuery('')
                      setPatient(record)
                    }
                  }
                }}
                loading={searching}
                dataSource={searchResults}
                columns={SystemUsersTemplates.columnTemplate([], 'patient', [], token, null, null, true).filter(
                  (i) => !i.hidden
                )}
              />
            </Col>
          </>
        ) : (
          <>
            <Col span={24}>
              You are about to enroll {patient.firstName} {patient.lastName} into the{' '}
              <span style={{ color: token.colorPrimary }}>{template.name}</span> {planLabel || 'plan'}.{' '}
            </Col>
            <Col span={24}>
              The enrollment date will be:{' '}
              <DatePicker
                allowClear={false}
                onChange={(date) => setEnrollmentOverride(date)}
                value={enrollmentOverride}
              />
            </Col>
            <Col span={24} style={{ marginTop: 24 }}>
              <Space>
                <Button
                  loading={enrolling}
                  type='primary'
                  style={{ background: token.colorSuccess }}
                  onClick={() => {
                    handleEnrollPatient()
                  }}
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => {
                    handleReset()
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Col>
          </>
        )}
      </CustomRow>
    </>
  )

  return (
    <CustomRow>
      <Col span={24}>{renderEnrolledPatients()}</Col>
      <Modal width={1920} destroyOnClose open={newEnrollment} footer={false} maskClosable={false} closable={false}>
        {renderEnrollmentSearch()}
      </Modal>
    </CustomRow>
  )
}

export default PatientEnrollment
